<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="title"
    :style="{ width: '800px' }"
    class="audio-trimmer-dialog"
    @hide="handleClose"
  >
    <div class="trimmer-container">
      <!-- 音频播放器 -->
      <div class="audio-player-section mb-4">
        <audio
          ref="audioRef"
          :src="audioUrl"
          class="w-full"
          @loadedmetadata="handleAudioLoaded"
          @timeupdate="handleTimeUpdate"
          @ended="handleAudioEnded"
        />
      </div>

      <!-- 音频信息 -->
      <div
        v-if="audioBuffer"
        class="audio-info-bar mb-2 flex items-center gap-4 text-xs text-surface-400"
      >
        <div class="flex items-center gap-1">
          <i class="pi pi-clock"></i>
          <span class="font-medium">时长：</span>
          <span>{{ formatTime(audioDuration) }}</span>
        </div>
        <div class="flex items-center gap-1">
          <i class="pi pi-wave-pulse"></i>
          <span class="font-medium">采样率：</span>
          <span>{{ audioBuffer.sampleRate + ' Hz' }}</span>
        </div>
        <div class="flex items-center gap-1">
          <i class="pi pi-volume-up"></i>
          <span class="font-medium">声道：</span>
          <span
            >{{ audioBuffer.numberOfChannels }}
            {{ audioBuffer.numberOfChannels > 1 ? '声道' : '声道' }}</span
          >
        </div>
      </div>

      <!-- 最大时长提示 -->
      <div
        v-if="maxDuration"
        class="max-duration-warning mb-2 p-2 rounded text-xs"
      >
        <i class="pi pi-info-circle mr-1"></i>
        <span
          >{{ t('workflowApps.maxDurationHint', { duration: maxDuration }) }}
        </span>
      </div>

      <!-- 波形可视化区域 -->
      <div class="waveform-section mb-4">
        <div class="waveform-container" @mousedown="handleWaveformClick">
          <canvas ref="waveformCanvasRef" class="waveform-canvas"></canvas>
          <!-- 裁剪区域选择 -->
          <div
            class="trim-overlay"
            :style="{
              left: `${trimStartPercent}%`,
              width: `${trimEndPercent - trimStartPercent}%`
            }"
          >
            <!-- 开始手柄 -->
            <div
              class="trim-handle trim-handle-start"
              @mousedown.stop="startDragging('start', $event)"
            >
              <div class="handle-line"></div>
            </div>
            <!-- 结束手柄 -->
            <div
              class="trim-handle trim-handle-end"
              @mousedown.stop="startDragging('end', $event)"
            >
              <div class="handle-line"></div>
            </div>
          </div>
          <!-- 播放位置指示器 -->
          <div
            v-if="currentTimePercent > 0"
            class="playback-indicator"
            :style="{ left: `${currentTimePercent}%` }"
          ></div>
        </div>
      </div>

      <!-- 控制区域 -->
      <div class="controls-section">
        <div class="flex items-center justify-between mb-2">
          <div class="time-display">
            <span class="text-sm text-surface-600">{{
              $t('audioTrimmer.start')
            }}</span>
            <span class="text-sm font-medium ml-2">{{
              formatTime(trimStart)
            }}</span>
          </div>
          <div class="time-display">
            <span class="text-sm text-surface-600">{{
              $t('audioTrimmer.end')
            }}</span>
            <span class="text-sm font-medium ml-2">{{
              formatTime(trimEnd)
            }}</span>
          </div>
          <div class="time-display">
            <span class="text-sm text-surface-600">{{
              $t('audioTrimmer.duration')
            }}</span>
            <span class="text-sm font-medium ml-2">{{
              formatTime(trimEnd - trimStart)
            }}</span>
          </div>
        </div>

        <!-- 播放控制按钮 -->
        <div class="flex items-center gap-2">
          <Button
            :icon="isPlaying ? 'pi pi-pause' : 'pi pi-play'"
            :label="
              isPlaying ? $t('audioTrimmer.pause') : $t('audioTrimmer.play')
            "
            size="small"
            @click="togglePlay"
          />
          <Button
            icon="pi pi-replay"
            :label="$t('audioTrimmer.reset')"
            size="small"
            severity="secondary"
            @click="resetTrim"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        :label="cancelLabel"
        severity="secondary"
        outlined
        @click="handleCancel"
      />
      <Button :label="noTrimLabel" severity="secondary" @click="handleNoTrim" />
      <Button :label="confirmLabel" @click="handleConfirm" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { FASTAPI_CONFIG } from '@/config/fastapi'

const { t } = useI18n()

interface Props {
  modelValue: boolean
  audioUrl: string
  maxDuration?: number
  title?: string
  cancelLabel?: string
  noTrimLabel?: string
  confirmLabel?: string
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', blob: Blob, duration: number): void
  (e: 'cancel'): void
  (e: 'no-trim'): void
}

const props = withDefaults(defineProps<Props>(), {
  title: '裁剪音频',
  cancelLabel: '取消',
  noTrimLabel: '不裁剪',
  confirmLabel: '确认裁剪'
})

const emit = defineEmits<Emits>()

// 响应式数据
const audioRef = ref<HTMLAudioElement>()
const waveformCanvasRef = ref<HTMLCanvasElement>()
const audioDuration = ref(0)
const trimStart = ref(0)
const trimEnd = ref(0)
const currentTime = ref(0)
const isPlaying = ref(false)
const audioBuffer = ref<AudioBuffer | null>(null)
const audioContext = ref<AudioContext | null>(null)

// 拖拽状态
const dragging = ref<'start' | 'end' | null>(null)

// 计算属性
const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const trimStartPercent = computed(() => {
  if (audioDuration.value === 0) return 0
  return (trimStart.value / audioDuration.value) * 100
})

const trimEndPercent = computed(() => {
  if (audioDuration.value === 0) return 100
  return (trimEnd.value / audioDuration.value) * 100
})

const currentTimePercent = computed(() => {
  if (audioDuration.value === 0) return 0
  return (currentTime.value / audioDuration.value) * 100
})

// 格式化时间（包含毫秒）
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000) // 显示三位毫秒
  return `${mins}:${secs.toString().padStart(2, '0')}:${ms.toString().padStart(3, '0')}`
}

// 音频加载完成
const handleAudioLoaded = async () => {
  if (!audioRef.value) return

  audioDuration.value = audioRef.value.duration
  trimStart.value = 0
  trimEnd.value = audioDuration.value

  // 加载音频数据并绘制波形
  await loadAudioBuffer()
  drawWaveform()
}

// 获取代理音频URL（用于波形加载，避免跨域）
const getProxyAudioUrl = (audioUrl: string): string => {
  if (!audioUrl) return ''

  // 如果是 blob URL，直接使用（浏览器本地临时 URL）
  if (audioUrl.startsWith('blob:')) {
    return audioUrl
  }

  // 如果已经是完整的http(s)链接
  if (audioUrl.startsWith('http://') || audioUrl.startsWith('https://')) {
    // 如果是OBS链接，使用代理接口
    if (audioUrl.includes('obs.cn-south-1.myhuaweicloud.com')) {
      // 提取文件名部分
      const urlParts = audioUrl.split('/')
      const filename = urlParts[urlParts.length - 1].split('?')[0] // 去掉可能存在的query参数
      // 使用后端代理URL
      return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${filename}?timestamp=${Date.now()}`
    }
    return audioUrl
  }

  // 如果只是文件名，使用后端代理URL
  return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${audioUrl}?timestamp=${Date.now()}`
}

// 加载音频缓冲区
const loadAudioBuffer = async () => {
  if (!props.audioUrl) return

  try {
    audioContext.value = new AudioContext()
    // 使用代理URL避免跨域问题
    const proxyUrl = getProxyAudioUrl(props.audioUrl)
    const response = await fetch(proxyUrl)
    const arrayBuffer = await response.arrayBuffer()
    audioBuffer.value = await audioContext.value.decodeAudioData(arrayBuffer)
  } catch (error) {
    console.error('加载音频失败:', error)
  }
}

// 绘制波形
const drawWaveform = () => {
  if (!waveformCanvasRef.value || !audioBuffer.value) return

  const canvas = waveformCanvasRef.value
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // 设置画布尺寸
  const container = canvas.parentElement
  if (container) {
    canvas.width = container.clientWidth
    canvas.height = 150
  }

  const width = canvas.width
  const height = canvas.height
  const data = audioBuffer.value.getChannelData(0)
  const step = Math.ceil(data.length / width)
  const amp = height / 2

  // 清空画布 - 深色背景
  ctx.fillStyle = '#0f172a'
  ctx.fillRect(0, 0, width, height)

  // 绘制网格线
  drawGrid(ctx, width, height)

  // 绘制中心线
  ctx.strokeStyle = 'rgba(148, 163, 184, 0.3)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(0, height / 2)
  ctx.lineTo(width, height / 2)
  ctx.stroke()

  // 创建波形渐变
  const gradient = ctx.createLinearGradient(0, 0, 0, height)
  gradient.addColorStop(0, 'rgba(99, 102, 241, 0.8)')
  gradient.addColorStop(0.5, 'rgba(139, 92, 246, 0.9)')
  gradient.addColorStop(1, 'rgba(99, 102, 241, 0.8)')

  // 绘制波形轮廓（使用填充）
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.moveTo(0, height / 2)

  // 上半部分波形
  const peaks: number[] = []
  for (let i = 0; i < width; i++) {
    let max = 0
    for (let j = 0; j < step; j++) {
      const datum = Math.abs(data[i * step + j] || 0)
      if (datum > max) max = datum
    }
    peaks.push(max)
    ctx.lineTo(i, height / 2 - max * amp * 0.9)
  }

  // 下半部分波形（镜像）
  for (let i = width - 1; i >= 0; i--) {
    ctx.lineTo(i, height / 2 + peaks[i] * amp * 0.9)
  }

  ctx.closePath()
  ctx.fill()

  // 绘制波形边缘线（更清晰的轮廓）
  ctx.strokeStyle = 'rgba(129, 140, 248, 1)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(0, height / 2)
  for (let i = 0; i < width; i++) {
    ctx.lineTo(i, height / 2 - peaks[i] * amp * 0.9)
  }
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(0, height / 2)
  for (let i = 0; i < width; i++) {
    ctx.lineTo(i, height / 2 + peaks[i] * amp * 0.9)
  }
  ctx.stroke()

  // 绘制时间标记
  drawTimeMarkers(ctx, width, height)
}

// 绘制网格线
const drawGrid = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
) => {
  // 主网格线
  ctx.strokeStyle = 'rgba(148, 163, 184, 0.15)'
  ctx.lineWidth = 1

  // 根据时长自动调整刻度数
  let timeSteps = 20
  if (audioDuration.value > 60) {
    timeSteps = 30
  } else if (audioDuration.value < 10) {
    timeSteps = 10
  }

  // 垂直网格线（时间刻度）
  for (let i = 0; i <= timeSteps; i++) {
    const x = (width / timeSteps) * i
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, height)
    // 每5条线加粗显示
    if (i % 5 === 0) {
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.25)'
      ctx.lineWidth = 1.5
    } else {
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.1)'
      ctx.lineWidth = 1
    }
    ctx.stroke()
  }

  // 水平网格线（振幅刻度）
  ctx.strokeStyle = 'rgba(148, 163, 184, 0.1)'
  ctx.lineWidth = 1
  const ampSteps = 6
  for (let i = 0; i <= ampSteps; i++) {
    const y = (height / ampSteps) * i
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(width, y)
    // 中心线加粗
    if (i === ampSteps / 2) {
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.3)'
      ctx.lineWidth = 1.5
    } else {
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.1)'
      ctx.lineWidth = 1
    }
    ctx.stroke()
  }
}

// 绘制时间标记
const drawTimeMarkers = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
) => {
  if (audioDuration.value === 0) return

  ctx.fillStyle = 'rgba(203, 213, 225, 0.9)'
  ctx.font = '11px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'center'

  // 根据时长决定显示的标记数量
  let markerSteps = 10
  if (audioDuration.value > 60) {
    markerSteps = 6 // 长音频少显示一些
  } else if (audioDuration.value < 10) {
    markerSteps = 5 // 短音频也少显示一些
  }

  for (let i = 0; i <= markerSteps; i++) {
    const x = (width / markerSteps) * i
    const time = (audioDuration.value / markerSteps) * i
    const timeStr = formatTimeDetailed(time)
    ctx.fillText(timeStr, x, height - 5)
  }
}

// 详细时间格式化（包含毫秒）
const formatTimeDetailed = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000) // 显示三位毫秒
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
}

// 播放/暂停
const togglePlay = () => {
  if (!audioRef.value) return

  if (isPlaying.value) {
    audioRef.value.pause()
    isPlaying.value = false
  } else {
    // 如果当前时间不在裁剪范围内，重置到开始位置
    if (
      audioRef.value.currentTime < trimStart.value ||
      audioRef.value.currentTime > trimEnd.value
    ) {
      audioRef.value.currentTime = trimStart.value
    }
    void audioRef.value.play()
    isPlaying.value = true
  }
}

// 时间更新
const handleTimeUpdate = () => {
  if (!audioRef.value) return

  currentTime.value = audioRef.value.currentTime

  // 如果播放到裁剪结束位置，暂停
  if (currentTime.value >= trimEnd.value && isPlaying.value) {
    audioRef.value.pause()
    audioRef.value.currentTime = trimStart.value
    isPlaying.value = false
  }
}

// 音频播放结束
const handleAudioEnded = () => {
  isPlaying.value = false
  if (audioRef.value) {
    audioRef.value.currentTime = trimStart.value
  }
}

// 点击波形跳转
const handleWaveformClick = (event: MouseEvent) => {
  if (!waveformCanvasRef.value || dragging.value) return

  const canvas = waveformCanvasRef.value
  const rect = canvas.getBoundingClientRect()
  const x = event.clientX - rect.left
  const percent = x / rect.width
  const newTime = percent * audioDuration.value

  if (audioRef.value) {
    audioRef.value.currentTime = Math.max(
      trimStart.value,
      Math.min(trimEnd.value, newTime)
    )
  }
}

// 开始拖拽
const startDragging = (handle: 'start' | 'end', event: MouseEvent) => {
  event.preventDefault()
  dragging.value = handle

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging.value || !waveformCanvasRef.value) return

    const canvas = waveformCanvasRef.value
    const rect = canvas.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    const percent = x / rect.width
    const newTime = percent * audioDuration.value

    if (dragging.value === 'start') {
      trimStart.value = Math.max(0, Math.min(newTime, trimEnd.value - 0.1))
    } else {
      trimEnd.value = Math.min(
        audioDuration.value,
        Math.max(newTime, trimStart.value + 0.1)
      )
    }
  }

  const handleMouseUp = () => {
    dragging.value = null
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

// 重置裁剪
const resetTrim = () => {
  trimStart.value = 0
  trimEnd.value = audioDuration.value
  if (audioRef.value) {
    audioRef.value.currentTime = 0
  }
}

// 确认裁剪
const handleConfirm = async () => {
  if (!audioBuffer.value || !audioContext.value) return

  try {
    // 计算裁剪的样本位置
    const sampleRate = audioBuffer.value.sampleRate
    const startSample = Math.floor(trimStart.value * sampleRate)
    const endSample = Math.floor(trimEnd.value * sampleRate)
    const newLength = endSample - startSample

    // 创建新的音频缓冲区
    const numberOfChannels = audioBuffer.value.numberOfChannels
    const newBuffer = audioContext.value.createBuffer(
      numberOfChannels,
      newLength,
      sampleRate
    )

    // 复制裁剪后的音频数据
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const oldData = audioBuffer.value.getChannelData(channel)
      const newData = newBuffer.getChannelData(channel)
      for (let i = 0; i < newLength; i++) {
        newData[i] = oldData[startSample + i]
      }
    }

    // 将音频缓冲区转换为 WAV Blob
    const blob = await audioBufferToWav(newBuffer)
    // 传递裁剪后的时长（秒）
    const trimmedDuration = trimEnd.value - trimStart.value
    emit('confirm', blob, trimmedDuration)
    visible.value = false
  } catch (error) {
    console.error('裁剪音频失败:', error)
  }
}

// 将 AudioBuffer 转换为 WAV Blob
const audioBufferToWav = async (buffer: AudioBuffer): Promise<Blob> => {
  const numberOfChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const format = 1 // PCM
  const bitDepth = 16

  const bytesPerSample = bitDepth / 8
  const blockAlign = numberOfChannels * bytesPerSample

  const data = new Float32Array(buffer.length * numberOfChannels)
  for (let channel = 0; channel < numberOfChannels; channel++) {
    const channelData = buffer.getChannelData(channel)
    for (let i = 0; i < buffer.length; i++) {
      data[i * numberOfChannels + channel] = channelData[i]
    }
  }

  const dataLength = data.length * bytesPerSample
  const bufferLength = 44 + dataLength
  const arrayBuffer = new ArrayBuffer(bufferLength)
  const view = new DataView(arrayBuffer)

  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataLength, true)
  writeString(view, 8, 'WAVE')

  // fmt sub-chunk
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, format, true) // audio format
  view.setUint16(22, numberOfChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true) // byte rate
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitDepth, true)

  // data sub-chunk
  writeString(view, 36, 'data')
  view.setUint32(40, dataLength, true)

  // write audio data
  floatTo16BitPCM(view, 44, data)

  return new Blob([arrayBuffer], { type: 'audio/wav' })
}

const writeString = (view: DataView, offset: number, string: string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i))
  }
}

const floatTo16BitPCM = (
  view: DataView,
  offset: number,
  input: Float32Array
) => {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
}

// 不裁剪 - 上传原音频
const handleNoTrim = () => {
  emit('no-trim')
  visible.value = false
}

// 取消
const handleCancel = () => {
  emit('cancel')
  visible.value = false
}

// 关闭对话框
const handleClose = () => {
  if (isPlaying.value && audioRef.value) {
    audioRef.value.pause()
    isPlaying.value = false
  }
  emit('cancel')
}

// 监听对话框打开状态
watch(
  () => props.modelValue,
  async (newValue) => {
    if (newValue && props.audioUrl) {
      await nextTick()
      // 重置状态
      trimStart.value = 0
      trimEnd.value = 0
      currentTime.value = 0
      isPlaying.value = false
    } else if (!newValue) {
      // 清理
      if (audioRef.value) {
        audioRef.value.pause()
        audioRef.value.currentTime = 0
      }
      isPlaying.value = false
      if (audioContext.value) {
        void audioContext.value.close()
        audioContext.value = null
      }
    }
  }
)

// 组件卸载时清理
onBeforeUnmount(() => {
  if (audioContext.value) {
    void audioContext.value.close()
  }
})
</script>

<style scoped>
.audio-trimmer-dialog :deep(.p-dialog-content) {
  padding: 1rem;
}

.trimmer-container {
  min-height: 300px;
}

.max-duration-warning {
  background: #fefce8;
  border: 1px solid #fef08a;
  color: #854d0e;
}

:global(.dark-theme) .max-duration-warning {
  background: rgba(113, 63, 18, 0.2);
  border-color: #854d0e;
  color: #fef08a;
}

.audio-player-section audio {
  width: 100%;
  margin-bottom: 1rem;
}

.waveform-section {
  position: relative;
  width: 100%;
}

.waveform-container {
  position: relative;
  width: 100%;
  height: 150px;
  background: #0f172a;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.3);
}

.waveform-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.trim-overlay {
  position: absolute;
  top: 0;
  height: 100%;
  background: rgba(34, 197, 94, 0.15);
  border-left: 3px solid #22c55e;
  border-right: 3px solid #22c55e;
  pointer-events: none;
  box-shadow: inset 0 0 20px rgba(34, 197, 94, 0.1);
}

.trim-handle {
  position: absolute;
  top: 0;
  width: 20px;
  height: 100%;
  cursor: ew-resize;
  pointer-events: all;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.trim-handle-start {
  left: -10px;
}

.trim-handle-end {
  right: -10px;
}

.handle-line {
  width: 4px;
  height: 50px;
  background: linear-gradient(to bottom, #22c55e, #16a34a);
  border-radius: 3px;
  box-shadow:
    0 2px 8px rgba(34, 197, 94, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: all 0.2s ease;
}

.trim-handle:hover .handle-line {
  height: 60px;
  box-shadow:
    0 4px 12px rgba(34, 197, 94, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.playback-indicator {
  position: absolute;
  top: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(to bottom, #ef4444, #dc2626);
  pointer-events: none;
  z-index: 10;
  box-shadow:
    0 0 10px rgba(239, 68, 68, 0.6),
    0 0 20px rgba(239, 68, 68, 0.3);
  animation: pulse-line 1.5s ease-in-out infinite;
}

@keyframes pulse-line {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.audio-info-bar {
  padding: 0.5rem;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 6px;
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.controls-section {
  margin-top: 1rem;
}

.time-display {
  display: flex;
  align-items: center;
}
</style>
